import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Paperclip, Link2, X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { apiUploadFile } from "../../api";
import { buildContractPdf, pdfName } from "../../lib/contractPdf";
import { toast } from "../../hooks/use-toast";
import ContractPages from "./ContractPages";

const canShareFiles = () => {
  try {
    const f = new File([new Blob(["x"])], "a.pdf", { type: "application/pdf" });
    return !!navigator.canShare && navigator.canShare({ files: [f] });
  } catch (e) {
    return false;
  }
};

// Envía el contrato por WhatsApp: adjuntando el PDF (celular) o con enlace al chat del cliente.
const SendWhatsApp = ({ contract, client }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const [readyFile, setReadyFile] = useState(null);
  const pageRefs = useRef([]);
  const num = (client?.whatsapp || "").replace(/\D/g, "");
  const greet = `Hola${client?.name ? ` ${client.name}` : ""}, te comparto tu contrato "${contract.name}".`;

  useEffect(() => {
    if (!mode) return;
    const t = setTimeout(async () => {
      try {
        const blob = (await buildContractPdf(pageRefs.current)).output("blob");
        const file = new File([blob], pdfName(contract.name), { type: "application/pdf" });
        if (mode === "attach") {
          setReadyFile(file);
          return;
        } else {
          const { url } = await apiUploadFile(file);
          const link = `${process.env.REACT_APP_BACKEND_URL}${url}`;
          const text = encodeURIComponent(`${greet}\n\nPuedes verlo y descargarlo aquí:\n${link}`);
          window.open(num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`, "_blank");
        }
        setOpen(false);
      } catch (e) {
        if (e?.name !== "AbortError") toast({ title: "No se pudo enviar el contrato", variant: "destructive" });
      } finally {
        setMode(null);
      }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const Option = ({ icon: Icon, title, desc, onClick, disabled, testId }) => (
    <button
      onClick={onClick}
      disabled={disabled || !!mode}
      className="flex w-full items-start gap-3 rounded-xl border border-slate-200 p-3 text-left transition hover:border-emerald-400 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
      data-testid={testId}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
      <span>
        <span className="block text-sm font-semibold text-slate-800">{title}</span>
        <span className="block text-xs text-slate-500">{desc}</span>
      </span>
    </button>
  );

  return (
    <>
      <button
        onClick={() => { setReadyFile(null); setOpen(true); }}
        className="flex items-center gap-1.5 rounded-md bg-[#25D366] px-3 py-2 text-sm font-semibold text-white transition hover:brightness-105"
        data-testid="contract-whatsapp"
      >
        <FaWhatsapp className="h-4 w-4" /> <span className="hidden sm:inline">WhatsApp</span>
      </button>
      {open &&
        createPortal(
          <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 sm:items-center" onClick={() => !mode && setOpen(false)}>
            <div className="w-full max-w-sm space-y-3 rounded-t-2xl bg-white p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="contract-whatsapp-sheet">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-bold text-slate-900">Enviar contrato por WhatsApp</h3>
                <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
              </div>
              <p className="text-xs text-slate-500">
                {client ? <>Cliente: <b>{client.name}</b>{num ? ` · ${client.whatsapp}` : " · sin WhatsApp registrado"}</> : "Este contrato no tiene cliente asignado."}
              </p>
              <Option
                icon={Paperclip}
                title="Adjuntar el PDF"
                desc={canShareFiles() ? "Abre WhatsApp con el archivo PDF adjunto; eliges el chat del cliente." : "Disponible desde el celular."}
                disabled={!canShareFiles()}
                onClick={() => setMode("attach")}
                testId="contract-wa-attach"
              />
              <Option
                icon={Link2}
                title={num ? "Enviar al chat del cliente" : "Enviar con enlace"}
                desc={num ? "Abre directo su chat con un enlace para ver y descargar el PDF." : "Abre WhatsApp con un enlace para ver y descargar el PDF."}
                onClick={() => setMode("link")}
                testId="contract-wa-link"
              />
              {readyFile && (
                <button
                  onClick={async () => {
                    try {
                      await navigator.share({ files: [readyFile], title: contract.name, text: greet });
                      setOpen(false);
                    } catch (e) {
                      if (e?.name !== "AbortError") toast({ title: "No se pudo compartir el PDF", variant: "destructive" });
                    } finally {
                      setReadyFile(null);
                    }
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] py-3 text-sm font-bold text-white"
                  data-testid="contract-wa-share-now"
                >
                  <FaWhatsapp className="h-5 w-5" /> Abrir WhatsApp con el PDF
                </button>
              )}
              {mode && (
                <p className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Preparando PDF…
                </p>
              )}
            </div>
          </div>,
          document.body
        )}
      {mode && (
        <div style={{ position: "fixed", left: -10000, top: 0 }} aria-hidden="true">
          <ContractPages contract={contract} pageRefs={pageRefs} />
        </div>
      )}
    </>
  );
};

export default SendWhatsApp;
